//JobMonitor.tsx
import React, { Component } from "react";
import { adriva_getJobs, adriva_getJobDetails, adriva_runPipeline, adriva_deleteJob, adriva_cancelPipeline } from "../API";
import { T } from "../theme/theme";
import FieldCompletionCard from "./FieldCompletionCard";

// ─── Types ────────────────────────────────────────────────────────────────────
type Toast = { id: string; title?: string; message: string; ok: boolean };
type FailedChunk = { chunk_index: number; columns: string[]; error: string; error_type: string; elapsed_sec: number };
type AgentStat = {
  endpoint: string; total_chunks: number; succeeded_chunks: number;
  failed_chunks_count: number; partial_result: boolean;
  elapsed_sec: number | null; failed_chunks: FailedChunk[];
};
type AgenticProcess = {
  name: string; status: string; start_time: string | null; end_time: string | null;
  agent_process_info: { agent_stat?: AgentStat; [k: string]: any } | null;
  aggregate_usage: { total_tokens: number; cost_usd: number } | null;
};
type State = {
  jobs: any[] | null; selectedJob: any | null;
  loading: boolean; jobLoading: boolean;
  jobTypeFilter: string; statusFilter: string; searchText: string;
  autoRefresh: boolean; liveMode: boolean; refreshId: any;
  changedMap: Record<string, number>; toasts: Toast[];
  expandedFields: Record<string, Set<string>>;
  dqStatusFilter: string; dqSourceFilter: string; dqColumnFilter: string;
  dqPage: number; dqPageSize: number;
  expandedAgents: Set<string>; expandedChunk: string | null;
  expandedThreads: Set<string>;
  expandedCalls:   Set<string>;
  agenticTab:      number;
  resuming:        string | null;   // job_id currently being resumed
  deleting:        string | null;   // job_id currently being deleted
  confirmDelete:   string | null;   // job_id awaiting delete confirmation
  cancelling:      string | null;   // job_id currently being cancelled
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const isSuccess = (s: string) => ["success","completed","done","partial"].includes((s||"").toLowerCase());
const isFail    = (s: string) => ["failed","fail","error"].includes((s||"").toLowerCase());
const isRunning = (s: string) => ["running","started","in_progress"].includes((s||"").toLowerCase());

const isStuck = (s: string, possiblyStuck?: boolean) =>
  possiblyStuck === true || false;

const statusMeta = (s: string, possiblyStuck?: boolean) => {
  const sl = (s||"").toLowerCase();
  if (sl === "cancelled")           return { dot: T.textMuted,bg: T.bg,        text: T.textMuted,border: T.border,       label: "CANCELLED" };
  if (sl === "partial")             return { dot: T.amber,    bg: T.amberBg,   text: T.amber,    border: T.amberBorder,  label: "PARTIAL" };
  if (isSuccess(sl))                return { dot: T.green,    bg: T.greenBg,   text: T.green,    border: T.greenBorder,  label: "SUCCESS" };
  if (isFail(sl))                   return { dot: T.red,      bg: T.redBg,     text: T.red,      border: T.redBorder,    label: "FAILED" };
  if (isStuck(sl, possiblyStuck))   return { dot: T.amber,    bg: T.amberBg,   text: T.amber,    border: T.amberBorder,  label: "STUCK" };
  if (isRunning(sl))                return { dot: T.brand,    bg: T.brandLight,text: T.brand,    border: T.brandMid,     label: "RUNNING" };
  return                                   { dot: T.textMuted, bg: T.bg,       text: T.textMuted,border: T.border,       label: sl.toUpperCase() || "—" };
};

const agentStatSummary = (stat?: AgentStat): "fail"|"partial"|"success"|null => {
  if (!stat) return null;
  if (stat.total_chunks > 0 && stat.succeeded_chunks === 0) return "fail";
  if (stat.partial_result) return "partial";
  return "success";
};

const distinct = (vs: string[]) => Array.from(new Set(vs.filter(Boolean))).sort();

const fmt = {
  // ★ Helper: ensure UTC timestamps are parsed correctly
  _parse: (d: string): Date => {
    // Backend sends UTC but sometimes without 'Z' — normalize
    let s = d.trim();
    if (!s.endsWith("Z") && !s.includes("+") && !s.includes("-", 10)) {
      s += "Z";  // treat bare timestamps as UTC
    }
    return new Date(s);
  },

  date: (d: string|null) => !d ? "—" : fmt._parse(d).toLocaleString(),

  time: (d: string|null) => !d ? "—" : fmt._parse(d).toLocaleTimeString([], {
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }),

  dur: (s?: string|null, e?: string|null) => {
    if (!s) return null;
    const sec = Math.round(((e ? fmt._parse(e) : new Date()).getTime() - fmt._parse(s).getTime()) / 1000);
    if (sec < 0) return "0s";
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.floor(sec/60)}m ${sec%60}s`;
    const hrs = Math.floor(sec / 3600);
    const mins = Math.floor((sec % 3600) / 60);
    return `${hrs}h ${mins}m`;
  },

  tok: (n?: number|null) => n == null ? "—" : n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n),
  usd: (n?: number|null) => n == null ? "—" : `$${n.toFixed(6)}`,
};


// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=Sora:wght@400;500;600;700&display=swap');
.jm*,.jm*::before,.jm*::after{box-sizing:border-box;margin:0;padding:0;}
.jm{font-family:'Sora',sans-serif;color:${T.textPrimary};display:flex;flex-direction:column;height:100%;background:${T.bg};font-size:13px;}

.jm-topbar{background:${T.brand};padding:0 20px;display:flex;align-items:center;gap:8px;height:50px;flex-shrink:0;}
.jm-topbar-title{font-size:14px;font-weight:700;color:#fff;letter-spacing:.3px;white-space:nowrap;}
.jm-topbar-sep{color:rgba(255,255,255,.35);font-size:16px;font-weight:300;}
.jm-topbar-entity{
  font-size:12.5px;font-weight:600;color:rgba(255,255,255,.95);
  background:rgba(255,255,255,.13);padding:3px 11px;border-radius:5px;
  border:1px solid rgba(255,255,255,.2);white-space:nowrap;
  max-width:240px;overflow:hidden;text-overflow:ellipsis;flex-shrink:1;
}
.jm-topbar-app{font-family:'IBM Plex Mono',monospace;font-size:10.5px;color:${T.brandMid};background:rgba(255,255,255,.07);padding:2px 8px;border-radius:4px;white-space:nowrap;}
.jm-spacer{flex:1;}

.jm-filters{background:${T.bgCard};border-bottom:1px solid ${T.border};padding:8px 20px;display:flex;align-items:center;gap:6px;flex-wrap:wrap;flex-shrink:0;}
.jm-count{font-size:11px;color:${T.textMuted};margin-left:auto;}

.jm-body{flex:1;min-height:0;display:grid;grid-template-columns:310px 1fr;}
.jm-left{background:${T.bgSidebar};border-right:1px solid ${T.border};display:flex;flex-direction:column;overflow:hidden;}
.jm-left-hdr{padding:9px 14px;font-size:10px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;color:${T.textMuted};border-bottom:1px solid ${T.border};display:flex;justify-content:space-between;align-items:center;flex-shrink:0;}
.jm-list{flex:1;overflow-y:auto;}
.jm-list::-webkit-scrollbar{width:3px;}.jm-list::-webkit-scrollbar-thumb{background:${T.border};border-radius:99px;}

.jm-row{padding:10px 14px;border-bottom:1px solid ${T.border};cursor:pointer;transition:background .1s;border-left:3px solid transparent;}
.jm-row:hover{background:${T.brandLight};border-left-color:${T.brandMid};}
.jm-row.sel{background:${T.brandLight};border-left-color:${T.brand};}
.jm-row.chg{animation:hl 3s ease forwards;}
@keyframes hl{0%{background:#fef9c3}100%{background:transparent}}

.jm-right{display:flex;flex-direction:column;overflow:hidden;}
.jm-detail-top{background:${T.bgCard};border-bottom:1px solid ${T.border};padding:13px 20px;display:flex;align-items:flex-start;justify-content:space-between;gap:12px;flex-shrink:0;}
.jm-detail-body{flex:1;overflow-y:auto;padding:16px 20px;}
.jm-detail-body::-webkit-scrollbar{width:4px;}.jm-detail-body::-webkit-scrollbar-thumb{background:${T.border};border-radius:99px;}

.jm-sec-title{font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:${T.brand};margin-bottom:10px;display:flex;align-items:center;gap:6px;}
.jm-sec-title::after{content:'';flex:1;height:1px;background:${T.brandLight};}
.jm-sec{margin-bottom:20px;}

.jm-kpis{display:flex;gap:8px;flex-wrap:wrap;}
.jm-kpi{flex:1;min-width:78px;padding:10px 12px;background:${T.bgCard};border:1px solid ${T.border};border-radius:8px;text-align:center;}
.jm-kpi-v{font-size:20px;font-weight:700;line-height:1;font-family:'IBM Plex Mono',monospace;}
.jm-kpi-l{font-size:10px;color:${T.textMuted};margin-top:3px;}

.jm-pill{display:inline-flex;align-items:center;padding:2px 8px;border-radius:99px;font-family:'IBM Plex Mono',monospace;font-size:10px;font-weight:500;border:1px solid;white-space:nowrap;}
.jm-dot-w{position:relative;display:inline-flex;align-items:center;justify-content:center;width:10px;height:10px;flex-shrink:0;}
.jm-dot{width:8px;height:8px;border-radius:50%;}
.jm-dot-ping{position:absolute;inset:0;border-radius:50%;animation:ping 1.6s ease infinite;}
@keyframes ping{75%,100%{transform:scale(2.2);opacity:0}}

.jm-card{background:${T.bgCard};border:1px solid ${T.border};border-radius:9px;overflow:hidden;margin-bottom:8px;}
.jm-card-hdr{padding:9px 13px;display:flex;align-items:center;cursor:pointer;transition:background .1s;gap:8px;}
.jm-card-hdr:hover{background:${T.bg};}
.jm-card-body{border-top:1px solid ${T.border};}

.jm-table{width:100%;border-collapse:collapse;font-size:12px;}
.jm-table th{padding:6px 10px;background:${T.bg};border-bottom:1px solid ${T.border};font-weight:600;color:${T.textLabel};text-align:left;position:sticky;top:0;z-index:1;font-size:11px;}
.jm-table td{padding:6px 10px;border-bottom:1px solid ${T.border};vertical-align:top;}
.jm-table tr:last-child td{border-bottom:none;}
.jm-table tr:hover td{background:${T.bg};}

.jm-scroll{max-height:280px;overflow-y:auto;border-radius:7px;border:1px solid ${T.border};}
.jm-scroll::-webkit-scrollbar{width:3px;}.jm-scroll::-webkit-scrollbar-thumb{background:${T.border};border-radius:99px;}

.jm-pre{font-family:'IBM Plex Mono',monospace;font-size:10.5px;color:${T.textSecond};max-height:90px;overflow:auto;white-space:pre-wrap;background:${T.bg};border:1px solid ${T.border};border-radius:5px;padding:5px 8px;}
.jm-hbar{height:5px;border-radius:99px;background:${T.border};overflow:hidden;}
.jm-hbar-fill{height:100%;border-radius:99px;transition:width .5s ease;}

.jm-ctrl{display:flex;flex-wrap:wrap;gap:6px;background:${T.bgCard};border:1px solid ${T.border};border-radius:8px;padding:8px 12px;margin-bottom:12px;}

.jm-astat{background:${T.bg};border:1px solid ${T.border};border-radius:8px;padding:12px 14px;}
.jm-chunk{background:${T.bgCard};border:1px solid ${T.redBorder};border-radius:7px;margin-top:8px;overflow:hidden;}
.jm-chunk-hdr{padding:7px 12px;display:flex;align-items:center;gap:8px;cursor:pointer;transition:background .1s;}
.jm-chunk-hdr:hover{background:${T.redBg};}
.jm-chunk-body{padding:10px 12px;border-top:1px solid ${T.redBorder};background:${T.redBg};}

.jm-agent-card{border:1px solid ${T.border};border-radius:8px;margin-bottom:7px;overflow:hidden;background:${T.bgCard};}
.jm-agent-hdr{padding:9px 12px;display:flex;align-items:center;gap:8px;cursor:pointer;transition:background .1s;}
.jm-agent-hdr:hover{background:${T.bg};}
.jm-agent-body{padding:12px;border-top:1px solid ${T.border};background:${T.bgSidebar};}

.jm-type{font-family:'IBM Plex Mono',monospace;font-size:9.5px;font-weight:500;padding:1px 7px;border-radius:4px;border:1px solid;}
.jm-type-ag{background:${T.purpleBg};color:${T.purple};border-color:${T.purpleBorder};}
.jm-type-dq{background:${T.brandLight};color:${T.brand};border-color:${T.brandMid};}
.jm-type-ot{background:${T.bg};color:${T.textMuted};border-color:${T.border};}

.jm-prog{height:2px;border-radius:99px;background:${T.border};margin-top:5px;overflow:hidden;}
.jm-prog-fill{height:100%;border-radius:99px;transition:width .5s;}

.jm-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:10px;color:${T.textMuted};text-align:center;padding:60px;}
.jm-ring{width:18px;height:18px;border-radius:50%;border:2px solid ${T.border};border-top-color:${T.brand};animation:spin .7s linear infinite;}
@keyframes spin{to{transform:rotate(360deg)}}

.jm-toasts{position:fixed;bottom:20px;right:20px;z-index:9999;display:flex;flex-direction:column;gap:6px;}
.jm-toast{padding:10px 14px;border-radius:8px;min-width:240px;display:flex;align-items:flex-start;gap:8px;font-family:'Sora',sans-serif;animation:slide .2s ease;box-shadow:0 4px 14px rgba(0,0,0,.1);}
@keyframes slide{from{opacity:0;transform:translateX(14px)}to{opacity:1;transform:none}}
.jm-toast.ok{background:${T.greenBg};border:1px solid ${T.greenBorder};color:${T.green};}
.jm-toast.err{background:${T.redBg};border:1px solid ${T.redBorder};color:${T.red};}

.jm-sel{padding:5px 9px;border:1px solid ${T.border};border-radius:6px;font-family:'Sora',sans-serif;font-size:12px;color:${T.textLabel};background:${T.bgSidebar};outline:none;cursor:pointer;}
.jm-sel:focus{border-color:${T.brand};}
.jm-inp{padding:5px 9px;border:1px solid ${T.border};border-radius:6px;font-family:'Sora',sans-serif;font-size:12px;color:${T.textPrimary};background:${T.bgCard};outline:none;min-width:170px;}
.jm-inp:focus{border-color:${T.brand};box-shadow:0 0 0 2px ${T.brandLight};}
.jm-btn{all:unset;box-sizing:border-box;display:inline-flex;align-items:center;gap:5px;padding:5px 11px;border-radius:6px;cursor:pointer;font-family:'Sora',sans-serif;font-size:12px;font-weight:500;border:1px solid ${T.border};background:${T.bgCard};color:${T.textLabel};transition:all .12s;white-space:nowrap;}
.jm-btn:hover{border-color:${T.brandMid};background:${T.brandLight};color:${T.brand};}
.jm-btn.on{background:${T.brandLight};color:${T.brand};border-color:${T.brandMid};}
.jm-btn:disabled{opacity:.35;cursor:not-allowed;}
.jm-btn-top{all:unset;box-sizing:border-box;display:inline-flex;align-items:center;gap:5px;padding:5px 11px;border-radius:6px;cursor:pointer;font-family:'Sora',sans-serif;font-size:12px;font-weight:500;border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.08);color:rgba(255,255,255,.85);transition:all .12s;white-space:nowrap;}
.jm-btn-top:hover{background:rgba(255,255,255,.15);border-color:rgba(255,255,255,.35);}
.jm-btn-top.on{background:rgba(255,255,255,.18);border-color:rgba(255,255,255,.4);}
.jm-btn-top:disabled{opacity:.35;cursor:not-allowed;}
.jm-pg{all:unset;box-sizing:border-box;padding:3px 8px;border:1px solid ${T.border};border-radius:5px;font-size:12px;cursor:pointer;background:${T.bgCard};color:${T.textLabel};}
.jm-pg:hover:not(:disabled){border-color:${T.brand};color:${T.brand};}
.jm-pg.cur{background:${T.brandLight};color:${T.brand};border-color:${T.brandMid};font-weight:600;}
.jm-pg:disabled{opacity:.3;cursor:not-allowed;}
.ag-tabs { display:flex; border-bottom:1px solid ${T.border}; margin-bottom:14px; }
.ag-tab  {
  padding:7px 14px; font-size:12px; font-weight:500; cursor:pointer;
  border-bottom:2px solid transparent; color:${T.textMuted}; background:none;
  border-top:none; border-left:none; border-right:none; transition:all .14s;
  font-family:'Sora',sans-serif;
}
.ag-tab:hover  { color:${T.textPrimary}; }
.ag-tab.active { color:${T.brand}; border-bottom-color:${T.brand}; font-weight:600; }
.ag-notice {
  background:${T.brandLight}; border:1px solid ${T.brandMid}; border-radius:8px;
  padding:10px 14px; font-size:12px; color:${T.brand};
  display:flex; gap:8px; align-items:flex-start; margin-bottom:14px;
}
.ag-thread {
  border:1px solid ${T.border}; border-radius:9px; overflow:hidden; margin-bottom:8px;
  background:${T.bgCard};
}
.ag-thread-hdr {
  padding:9px 13px; display:flex; align-items:center; gap:8px; cursor:pointer;
  background:${T.bgSidebar}; transition:background .12s;
}
.ag-thread-hdr:hover { background:${T.brandLight}; }
.ag-thread-body { border-top:1px solid ${T.border}; padding:12px; }
.ag-agent { border:1px solid ${T.border}; border-radius:7px; overflow:hidden; margin-bottom:7px; }
.ag-agent-hdr { padding:7px 10px; display:flex; align-items:center; gap:7px; cursor:pointer; background:${T.bg}; transition:background .12s; }
.ag-agent-hdr:hover { background:${T.brandLight}; }
.ag-agent-body { border-top:1px solid ${T.border}; padding:10px; }
.ag-call { border:1px solid ${T.border}; border-radius:7px; overflow:hidden; margin-bottom:6px; }
.ag-call-hdr { padding:7px 10px; display:flex; align-items:center; gap:7px; cursor:pointer; background:${T.bg}; transition:background .12s; }
.ag-call-hdr:hover { background:${T.brandLight}; }
.ag-call-body { border-top:1px solid ${T.border}; padding:10px; }
.ag-prompt {
  font-family:'IBM Plex Mono',monospace; font-size:10.5px; color:${T.textMuted};
  background:${T.bgSidebar}; border:1px solid ${T.border}; border-radius:6px;
  padding:8px 10px; white-space:pre-wrap; word-break:break-word;
  max-height:160px; overflow-y:auto; line-height:1.5;
}
.ag-output {
  font-family:'IBM Plex Mono',monospace; font-size:10.5px; color:${T.green};
  background:${T.greenBg}; border:1px solid ${T.greenBorder}; border-radius:6px;
  padding:8px 10px; white-space:pre-wrap; word-break:break-word;
  max-height:160px; overflow-y:auto; line-height:1.5;
}
.ag-warn {
  background:${T.amberBg}; border:1px solid ${T.amberBorder}; border-radius:6px;
  padding:8px 12px; font-size:11px; color:${T.amber}; margin-bottom:10px;
  display:flex; gap:6px; align-items:flex-start;
}
.ag-tbl { width:100%; border-collapse:collapse; font-size:12px; }
.ag-tbl th { padding:6px 8px; background:${T.bg}; border-bottom:1px solid ${T.border}; font-weight:600; color:${T.textMuted}; text-align:left; font-size:11px; position:sticky; top:0; }
.ag-tbl td { padding:6px 8px; border-bottom:1px solid ${T.border}; vertical-align:top; }
.ag-tbl tr:last-child td { border:none; }
.ag-tbl tr:hover td { background:${T.bg}; }
`;

// ─── Sub-components ───────────────────────────────────────────────────────────
function Pill({ s, style, possiblyStuck }: { s: string; style?: React.CSSProperties; possiblyStuck?: boolean }) {
  const m = statusMeta(s, possiblyStuck);
  return <span className="jm-pill" style={{ background: m.bg, color: m.text, borderColor: m.border, ...style }}>{m.label}</span>;
}
function Dot({ s, possiblyStuck }: { s: string; possiblyStuck?: boolean }) {
  const m = statusMeta(s, possiblyStuck);
  return (
    <span className="jm-dot-w">
      {isRunning(s) && <span className="jm-dot-ping" style={{ background: m.dot, opacity: .2 }} />}
      <span className="jm-dot" style={{ background: m.dot }} />
    </span>
  );
}

function TypeBadge({ jt }: { jt: string }) {
  const t = (jt||"").toLowerCase();
  const cls = t.includes("dq")||t.includes("rule") ? "jm-type jm-type-dq"
    : t.includes("metadata")||t.includes("cde")||t.includes("compliance")||t.includes("lineage") ? "jm-type jm-type-ag"
    : "jm-type jm-type-ot";
  return <span className={cls}>{jt||"JOB"}</span>;
}

function RetryChip({ rs }: { rs: RetryStats }) {
  const recovered = rs.recovered_on_retry ?? 0;
  const missing   = rs.still_missing ?? 0;

  // agent gave up on some fields — the important failure signal
  if (missing > 0) {
    return (
      <span className="jm-pill"
        style={{ background: T.redBg, color: T.red, borderColor: T.redBorder, fontSize: 9.5 }}
        title={`Agent retried ${rs.retry_attempts ?? 0}× · recovered ${recovered} · ${missing} still missing (sweep should catch these)`}>
        ⚠ {missing} unrecovered
      </span>
    );
  }
  // retry fired and saved the chunk — mirrors the sweep chip's amber
  if (recovered > 0) {
    return (
      <span className="jm-pill"
        style={{ background: T.amberBg, color: T.amber, borderColor: T.amberBorder, fontSize: 9.5 }}
        title={`Agent dropped ${rs.first_pass_missing ?? recovered} field(s) on first pass, recovered on retry`}>
        ↻ retried: {recovered} recovered
      </span>
    );
  }
  return null; // ok_first_pass → no chip, no noise
}

// ─── Agent Stat Panel ─────────────────────────────────────────────────────────
function AgentStatPanel({ stat, expandedChunk, onToggle }: {
  stat: AgentStat; expandedChunk: string|null; onToggle: (k: string) => void;
}) {
  const summary = agentStatSummary(stat)!;
  const pct     = stat.total_chunks > 0 ? Math.round(stat.succeeded_chunks / stat.total_chunks * 100) : 100;
  const barC    = summary === "fail" ? T.red : summary === "partial" ? T.amber : T.green;
  return (
    <div className="jm-astat">
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10, flexWrap:"wrap" }}>
        <span style={{ fontWeight:600, fontSize:12, color:T.textLabel }}>Agent execution</span>
        <Pill s={summary} />
        {stat.endpoint && (
          <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:T.textMuted }}>
            {stat.endpoint.split("/").pop()}
          </span>
        )}
        {stat.elapsed_sec != null && (
          <span style={{ marginLeft:"auto", fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:T.textMuted }}>
            {stat.elapsed_sec}s
          </span>
        )}
      </div>
      <div className="jm-kpis" style={{ marginBottom:10 }}>
        {[
          { v: stat.total_chunks,        l: "chunks",    c: T.textPrimary },
          { v: stat.succeeded_chunks,    l: "succeeded", c: T.green },
          { v: stat.failed_chunks_count, l: "failed",    c: stat.failed_chunks_count > 0 ? T.red : T.textMuted },
        ].map((k,i) => (
          <div className="jm-kpi" key={i}>
            <div className="jm-kpi-v" style={{ color:k.c }}>{k.v}</div>
            <div className="jm-kpi-l">{k.l}</div>
          </div>
        ))}
      </div>
      <div style={{ marginBottom: stat.failed_chunks?.length ? 12 : 0 }}>
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:T.textMuted, marginBottom:3 }}>
          <span>Chunk success rate</span>
          <span style={{ fontWeight:600, color:barC, fontFamily:"'IBM Plex Mono',monospace" }}>{pct}%</span>
        </div>
        <div className="jm-hbar"><div className="jm-hbar-fill" style={{ width:`${pct}%`, background:barC }} /></div>
      </div>
      {stat.failed_chunks?.length > 0 && (
        <>
          <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".8px", color:T.red, margin:"10px 0 4px" }}>
            Failed chunks ({stat.failed_chunks.length})
          </div>
          {stat.failed_chunks.map(fc => {
            const key  = `${fc.chunk_index}`;
            const open = expandedChunk === key;
            const cols = (fc.columns||[]).join(", ") || "—";
            return (
              <div className="jm-chunk" key={key}>
                <div className="jm-chunk-hdr" onClick={() => onToggle(key)}>
                  <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:T.red, fontWeight:600 }}>#{fc.chunk_index}</span>
                  <span className="jm-pill" style={{ background:T.redBg, color:T.red, borderColor:T.redBorder, fontSize:9.5 }}>{fc.error_type}</span>
                  <span style={{ fontSize:11, color:T.textMuted, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{cols}</span>
                  <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:T.textMuted, flexShrink:0 }}>{fc.elapsed_sec}s</span>
                  <span style={{ fontSize:11, color:T.textMuted }}>{open?"▲":"▼"}</span>
                </div>
                {open && (
                  <div className="jm-chunk-body">
                    {fc.columns?.length > 0 && (
                      <div style={{ marginBottom:8 }}>
                        <div style={{ fontSize:10, fontWeight:600, color:T.textMuted, marginBottom:4 }}>Affected columns</div>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                          {fc.columns.map((c,i) => (
                            <span key={i} className="jm-pill" style={{ background:T.amberBg, color:T.amber, borderColor:T.amberBorder }}>{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize:10, fontWeight:600, color:T.textMuted, marginBottom:3 }}>Error message</div>
                      <div className="jm-pre" style={{ color:T.red, maxHeight:70 }}>{fc.error}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
      {!stat.failed_chunks?.length && (
        <div style={{ fontSize:11, color:T.green, textAlign:"center", paddingTop:8 }}>All chunks completed successfully</div>
      )}
    </div>
  );
}

// ─── DQ Rules section ─────────────────────────────────────────────────────────
function DQSection({ job, expandedFields, onToggleField, dqStatusFilter, dqSourceFilter, dqColumnFilter, dqPage, dqPageSize, onFilter, onPage }: {
  job: any; expandedFields: Record<string,Set<string>>;
  onToggleField: (jid:string, f:string) => void;
  dqStatusFilter:string; dqSourceFilter:string; dqColumnFilter:string;
  dqPage:number; dqPageSize:number;
  onFilter:(k:string,v:string)=>void; onPage:(n:number)=>void;
}) {
  const [mode, setMode] = React.useState<"grouped"|"flat">("grouped");
  const rules: any[] = job.rules || [];
  if (!rules.length) return <div style={{ textAlign:"center", padding:20, color:T.textMuted, fontSize:12 }}>No rule execution data.</div>;

  const flat = rules.map(r => ({
    rule_id: r.rule_id||r.id||"—",
    column_name: r.column_name||r.columnName||"—",
    source_prefix: r.source_prefix||r.sourcePrefix||"—",
    result_status: (r.result_status||r.status||"").toUpperCase(),
    raw: r,
  }));
  const sources = distinct(flat.map(r => r.source_prefix));
  const columns = distinct(flat.map(r => r.column_name));

  const filtered = flat.filter(r => {
    if (dqStatusFilter !== "all" && r.result_status !== dqStatusFilter) return false;
    if (dqSourceFilter !== "all" && r.source_prefix  !== dqSourceFilter) return false;
    if (dqColumnFilter !== "all" && r.column_name    !== dqColumnFilter) return false;
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / dqPageSize));
  const pageItems  = filtered.slice((dqPage-1)*dqPageSize, dqPage*dqPageSize);

  const grouped: Record<string,{total:number;pass:number;fail:number;sources:Record<string,any[]>}> = {};
  filtered.forEach(r => {
    const f = r.column_name, s = r.source_prefix;
    if (!grouped[f]) grouped[f] = {total:0,pass:0,fail:0,sources:{}};
    if (!grouped[f].sources[s]) grouped[f].sources[s] = [];
    grouped[f].sources[s].push(r);
    grouped[f].total++;
    if (r.result_status==="PASS") grouped[f].pass++;
    if (r.result_status==="FAIL") grouped[f].fail++;
  });

  return (
    <>
      <div className="jm-ctrl">
        <select className="jm-sel" value={dqStatusFilter} onChange={e=>{onFilter("dqStatusFilter",e.target.value);onPage(1);}}>
          <option value="all">All statuses</option>
          <option value="PASS">PASS</option>
          <option value="FAIL">FAIL</option>
          <option value="UNKNOWN">UNKNOWN</option>
        </select>
        <select className="jm-sel" value={dqSourceFilter} onChange={e=>{onFilter("dqSourceFilter",e.target.value);onPage(1);}}>
          <option value="all">All sources</option>
          {sources.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <select className="jm-sel" value={dqColumnFilter} onChange={e=>{onFilter("dqColumnFilter",e.target.value);onPage(1);}}>
          <option value="all">All columns</option>
          {columns.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <button className="jm-btn" onClick={()=>setMode(m=>m==="grouped"?"flat":"grouped")}>
          {mode==="grouped"?"Flat view":"Grouped view"}
        </button>
        <span style={{ marginLeft:"auto", fontSize:11, color:T.textMuted }}>{filtered.length} rules</span>
      </div>

      {mode==="grouped" ? (
        Object.entries(grouped).map(([field, info]) => {
          const open = expandedFields[job.job_id]?.has(field);
          const pct  = info.total > 0 ? Math.round(info.pass/info.total*100) : 0;
          return (
            <div className="jm-card" key={field}>
              <div className="jm-card-hdr" onClick={()=>onToggleField(job.job_id, field)}>
                <span style={{ fontWeight:600, fontSize:13, color:T.textPrimary, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{field}</span>
                <span style={{ fontSize:10, color:T.textMuted, background:T.bg, padding:"1px 6px", borderRadius:99, flexShrink:0 }}>{info.total}</span>
                <span style={{ fontSize:11, color:T.green, flexShrink:0 }}>✓ {info.pass}</span>
                <span style={{ fontSize:11, color:T.red, flexShrink:0 }}>✗ {info.fail}</span>
                <div style={{ width:44, height:3, background:T.border, borderRadius:99, overflow:"hidden", flexShrink:0 }}>
                  <div style={{ height:"100%", width:`${pct}%`, background:T.green, borderRadius:99 }} />
                </div>
                <span style={{ fontSize:11, color:T.textMuted }}>{open?"▲":"▼"}</span>
              </div>
              {open && (
                <div className="jm-card-body">
                  {Object.entries(info.sources).map(([src, srules]:any) => (
                    <div key={src} style={{ padding:"8px 13px 0" }}>
                      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10.5, fontWeight:600, color:T.brand, marginBottom:5 }}>{src}</div>
                      <div style={{ overflowX:"auto", marginBottom:8 }}>
                        <table className="jm-table">
                          <thead><tr><th style={{width:130}}>Rule ID</th><th style={{width:80}}>Status</th><th>Details</th></tr></thead>
                          <tbody>
                            {srules.map((r:any, i:number) => {
                              const m = statusMeta((r.result_status||"").toLowerCase());
                              return (
                                <tr key={i}>
                                  <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10.5 }}>{r.rule_id}</td>
                                  <td><span className="jm-pill" style={{ background:m.bg, color:m.text, borderColor:m.border }}>{r.result_status}</span></td>
                                  <td><pre className="jm-pre">{JSON.stringify({metrics:r.raw?.metrics, validation:r.raw?.validation_results},null,2)}</pre></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      ) : (
        <>
          <div className="jm-scroll">
            <table className="jm-table">
              <thead><tr><th style={{width:120}}>Rule ID</th><th style={{width:140}}>Column</th><th style={{width:110}}>Source</th><th style={{width:75}}>Status</th><th>Details</th></tr></thead>
              <tbody>
                {pageItems.map((r,i) => {
                  const m = statusMeta(r.result_status.toLowerCase());
                  return (
                    <tr key={i}>
                      <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10.5 }}>{r.rule_id}</td>
                      <td>{r.column_name}</td>
                      <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10.5 }}>{r.source_prefix}</td>
                      <td><span className="jm-pill" style={{ background:m.bg, color:m.text, borderColor:m.border }}>{r.result_status}</span></td>
                      <td><pre className="jm-pre">{JSON.stringify({metrics:r.raw?.metrics},null,2)}</pre></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:8, flexWrap:"wrap", gap:5 }}>
            <span style={{ fontSize:11, color:T.textMuted }}>{filtered.length} results</span>
            <div style={{ display:"flex", gap:3 }}>
              <button className="jm-pg" disabled={dqPage===1}           onClick={()=>onPage(1)}>«</button>
              <button className="jm-pg" disabled={dqPage===1}           onClick={()=>onPage(dqPage-1)}>‹</button>
              {Array.from({length:Math.min(5,totalPages)}).map((_,i)=>{
                const base=Math.max(1,Math.min(dqPage-2,totalPages-4)), p=base+i;
                return p<=totalPages ? <button key={p} className={`jm-pg${p===dqPage?" cur":""}`} onClick={()=>onPage(p)}>{p}</button> : null;
              })}
              <button className="jm-pg" disabled={dqPage===totalPages} onClick={()=>onPage(dqPage+1)}>›</button>
              <button className="jm-pg" disabled={dqPage===totalPages} onClick={()=>onPage(totalPages)}>»</button>
            </div>
            <select className="jm-sel" value={dqPageSize} onChange={e=>onFilter("dqPageSize",e.target.value)}>
              <option value={25}>25/pg</option><option value={50}>50/pg</option><option value={100}>100/pg</option>
            </select>
          </div>
        </>
      )}
    </>
  );
}

// ─── Agentic processes section ────────────────────────────────────────────────
function AgenticSection({ processes, expandedAgents, onToggle, expandedChunk, onToggleChunk }: {
  processes: AgenticProcess[]; expandedAgents: Set<string>;
  onToggle: (k:string)=>void; expandedChunk:string|null; onToggleChunk:(k:string)=>void;
}) {
  if (!processes?.length) return <div style={{ textAlign:"center", padding:16, color:T.textMuted, fontSize:12 }}>No agentic process data.</div>;
  return (
    <>
      {processes.map((ap, idx) => {
        const key  = `${ap.name}-${idx}`;
        const open = expandedAgents.has(key);
        const stat = ap.agent_process_info?.agent_stat;
        const ss   = agentStatSummary(stat);
        const usage = ap.aggregate_usage;
        return (
          <div className="jm-agent-card" key={key}>
            <div className="jm-agent-hdr" onClick={()=>onToggle(key)}>
              <Dot s={ap.status} />
              <span style={{ fontWeight:600, fontSize:12.5, color:T.textPrimary, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ap.name}</span>
              <Pill s={ap.status} style={{ fontSize:9.5 }} />
              {ss && ss !== "success" && <Pill s={ss} style={{ fontSize:9.5 }} />}
              {usage && (
                <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:T.textMuted }}>
                  {fmt.tok(usage.total_tokens)} · {fmt.usd(usage.cost_usd)}
                </span>
              )}
              <span style={{ fontSize:11, color:T.textMuted }}>{open?"▲":"▼"}</span>
            </div>
            {open && (
              <div className="jm-agent-body">
                <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:10, fontSize:11, color:T.textMuted }}>
                  {ap.start_time && <span>Start: <b style={{color:T.textLabel}}>{fmt.time(ap.start_time)}</b></span>}
                  {ap.end_time   && <span>End: <b style={{color:T.textLabel}}>{fmt.time(ap.end_time)}</b></span>}
                  {ap.start_time && <span>Duration: <b style={{color:T.textLabel}}>{fmt.dur(ap.start_time, ap.end_time)}</b></span>}
                </div>
                {usage && (
                  <div className="jm-kpis" style={{ marginBottom:12 }}>
                    <div className="jm-kpi"><div className="jm-kpi-v" style={{ color:T.brand, fontSize:15 }}>{fmt.tok(usage.total_tokens)}</div><div className="jm-kpi-l">tokens</div></div>
                    <div className="jm-kpi"><div className="jm-kpi-v" style={{ color:T.green, fontSize:15 }}>{fmt.usd(usage.cost_usd)}</div><div className="jm-kpi-l">cost</div></div>
                  </div>
                )}
                {stat
                  ? <AgentStatPanel stat={stat} expandedChunk={expandedChunk} onToggle={onToggleChunk} />
                  : <div style={{ fontSize:11, color:T.textMuted, fontStyle:"italic" }}>No chunk-level stat available.</div>
                }
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// ─── Detail body ──────────────────────────────────────────────────────────────
function DetailBody({ job, st, appNm, onResumed, onToggleField, onToggleAgent, onToggleChunk, onFilter, onPage,
  onToggleThread, onToggleCall, onSetAgenticTab,
}: {
  job:any; st:State; appNm:string; onResumed:()=>void;
  onToggleField:(jid:string,f:string)=>void;
  onToggleAgent:(k:string)=>void;
  onToggleChunk:(k:string)=>void;
  onFilter:(k:string,v:string)=>void;
  onPage:(n:number)=>void;
  onToggleThread:(k:string)=>void;
  onToggleCall:(k:string)=>void;
  onSetAgenticTab:(n:number)=>void;
}) {
  const hasAgentic = isAgenticJob(job);
  const hasDQ      = isDQJob(job);

  const kpis: {v:any;l:string;c:string}[] = [];
  if (hasDQ) {
    if (job.total_rules  != null) kpis.push({v:job.total_rules, l:"total rules", c:T.textPrimary});
    if (job.pass_count   != null) kpis.push({v:job.pass_count,  l:"passed",      c:T.green});
    if (job.fail_count   != null) kpis.push({v:job.fail_count,  l:"failed",      c:T.red});
    if (job.skip_count   > 0)     kpis.push({v:job.skip_count,  l:"skipped",     c:T.amber});
    if (job.error_count  > 0)     kpis.push({v:job.error_count, l:"errors",      c:T.red});
    if (job.std_count    > 0)     kpis.push({v:job.std_count,   l:"standardized",c:T.brand});
  }

  return (
    <div className="jm-detail-body">
      {kpis.length > 0 && (
        <div className="jm-sec">
          <div className="jm-sec-title">Summary</div>
          <div className="jm-kpis">
            {kpis.map((k,i)=>(
              <div className="jm-kpi" key={i}>
                <div className="jm-kpi-v" style={{color:k.c}}>{k.v??0}</div>
                <div className="jm-kpi-l">{k.l}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <FieldCompletionCard
        job={job}
        appNm={appNm}
        onResumed={onResumed}
      />
      {hasAgentic && (
        <AgenticDetailPanel
          job={job}
          expandedThreads={st.expandedThreads}
          expandedAgents={st.expandedAgents}
          expandedCalls={st.expandedCalls}
          agenticTab={st.agenticTab}
          onToggleThread={onToggleThread}
          onToggleAgent={onToggleAgent}
          onToggleCall={onToggleCall}
          onSetTab={onSetAgenticTab}
        />
      )}
      {hasDQ && (
        <div className="jm-sec">
          <div className="jm-sec-title">Rule execution</div>
          <DQSection
            job={job} expandedFields={st.expandedFields} onToggleField={onToggleField}
            dqStatusFilter={st.dqStatusFilter} dqSourceFilter={st.dqSourceFilter}
            dqColumnFilter={st.dqColumnFilter} dqPage={st.dqPage} dqPageSize={st.dqPageSize}
            onFilter={onFilter} onPage={onPage}
          />
        </div>
      )}
      {!hasAgentic && !hasDQ && (
        <div style={{ padding:24, textAlign:"center", color:T.textMuted, fontSize:12 }}>
          No detailed execution data available for this job type.
        </div>
      )}
      {job.config && Object.keys(job.config).filter(k=>k!=="agent_stat").length > 0 && (
        <div className="jm-sec">
          <div className="jm-sec-title">Config</div>
          <pre className="jm-pre" style={{ maxHeight:110 }}>
            {JSON.stringify(Object.fromEntries(Object.entries(job.config).filter(([k])=>k!=="agent_stat")),null,2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ─── Main class ───────────────────────────────────────────────────────────────
export default class JobMonitor extends Component<{
  appNm:                 string;
  entityName?:           string;
  selectedEntityId?:     string;
  initialJobs?:          any[] | null;
  initialStatusFilter?:  string;
  initialJobTypeFilter?: string;
  initialSelectedJobId?: string;
  onRefresh?:            () => void;
  /** @deprecated — resume is now triggered directly in the monitor */
  onNavigateToRun?:      () => void;
}, State> {

  prevMap: Record<string, any> = {};
  hlT:     Record<string, any> = {};

  state: State = {
    jobs: null, selectedJob: null, loading: false, jobLoading: false,
    jobTypeFilter: "all", statusFilter: "all", searchText: "",
    autoRefresh: false, liveMode: false, refreshId: null,
    changedMap: {}, toasts: [],
    expandedFields: {},
    dqStatusFilter: "all", dqSourceFilter: "all", dqColumnFilter: "all",
    dqPage: 1, dqPageSize: 50,
    expandedAgents: new Set(), expandedChunk: null,
    expandedThreads: new Set(),
    expandedCalls:   new Set(),
    agenticTab:      0,
    resuming:        null,
    deleting:        null,
    confirmDelete:   null,
    cancelling:      null,
  };

  toggleThread  = (k: string) => this.setState(p => { const s = new Set(p.expandedThreads); s.has(k)?s.delete(k):s.add(k); return {expandedThreads:s}; });
  toggleCall    = (k: string) => this.setState(p => { const s = new Set(p.expandedCalls);   s.has(k)?s.delete(k):s.add(k); return {expandedCalls:s}; });
  setAgenticTab = (n: number) => this.setState({ agenticTab: n });

  // ── Resume a stuck/failed job directly from the monitor ──────────────────
  triggerResume = async (job: any) => {
    const { appNm } = this.props;
    const jobId = job.job_id;
    if (!jobId || !job.process_id) return;

    this.setState({ resuming: jobId });

    // Find all jobs in the same process to determine done/pending types
    const processJobs = (this.state.jobs || []).filter(j => j.process_id === job.process_id);
    const TERMINAL_OK = new Set(["success", "completed", "done"]);
    const TERMINAL_BAD = new Set(["failed", "fail", "error", "cancelled"]);
    const NOT_STARTED = new Set(["yet_to_start", "pending", "queued"]);

    const doneTypes    = processJobs.filter(j => TERMINAL_OK.has((j.status||"").toLowerCase())).map(j => j.job_type).filter(Boolean);
    const pendingTypes = processJobs.filter(j =>
      TERMINAL_BAD.has((j.status||"").toLowerCase()) ||
      ["running","started","in_progress"].includes((j.status||"").toLowerCase()) ||
      NOT_STARTED.has((j.status||"").toLowerCase())
    ).map(j => j.job_type).filter(Boolean);

    try {
      // ★ Always include at least THIS job's type, even if process scan missed it
      const thisJobType = job.job_type;
      const finalTypes = pendingTypes.length > 0
        ? pendingTypes
        : (thisJobType ? [thisJobType] : undefined);

      const res = await adriva_runPipeline(
        appNm,
        job.entity_id || job.dm_id,
        job.process_id,
        [thisJobType],
        true,
      );

      if (res.success) {
        this.setState(p => ({
          resuming: null,
          toasts: [...p.toasts, {
            id: `resume-${Date.now()}`,
            title: "Resume started",
            //message: `Skipping ${doneTypes.length} done job(s) · running ${(finalTypes || ["all remaining"]).join(", ")}`,
            message: `Resuming ${job.job_type} from checkpoint`,
            ok: true,
          }],
        }));
        // Reload the job list after a short delay so new jobs appear
        setTimeout(() => this.load(appNm, true), 2000);
      } else {
        this.setState(p => ({
          resuming: null,
          toasts: [...p.toasts, {
            id: `resume-err-${Date.now()}`,
            title: "Resume failed",
            message: res.message || "Could not start resume",
            ok: false,
          }],
        }));
      }
    } catch (e: any) {
      this.setState(p => ({
        resuming: null,
        toasts: [...p.toasts, {
          id: `resume-err-${Date.now()}`,
          title: "Resume error",
          message: e.message || "Network error",
          ok: false,
        }],
      }));
    }
  };

  triggerCancel = async (job: any) => {
    const entityId = job.entity_id || job.dm_id || this.props.selectedEntityId;
    if (!entityId) return;
    const jobId = job.job_id;
    this.setState({ cancelling: jobId });
    try {
      const res = await adriva_cancelPipeline(entityId);
      if (res.success) {
        this.setState(p => ({
          cancelling: null,
          toasts: [...p.toasts, {
            id: `cancel-${Date.now()}`,
            title: "Pipeline stopped",
            message: `Cancelled for entity ${entityId.substring(0, 12)}…`,
            ok: true,
          }],
        }));
        setTimeout(() => this.refreshData(false), 1500);
      } else {
        this.setState(p => ({
          cancelling: null,
          toasts: [...p.toasts, {
            id: `cancel-err-${Date.now()}`,
            title: "Cancel failed",
            message: res.message || "Could not cancel",
            ok: false,
          }],
        }));
      }
    } catch (e: any) {
      this.setState(p => ({
        cancelling: null,
        toasts: [...p.toasts, {
          id: `cancel-err-${Date.now()}`,
          title: "Cancel error",
          message: e.message || "Network error",
          ok: false,
        }],
      }));
    }
  };

  // ── Delete a job from the list ──────────────────────────────────────────
  deleteJob = async (jobId: string) => {
    // First click: show confirmation
    if (this.state.confirmDelete !== jobId) {
      this.setState({ confirmDelete: jobId });
      // Auto-cancel confirmation after 4s
      setTimeout(() => {
        this.setState(p => ({ confirmDelete: p.confirmDelete === jobId ? null : p.confirmDelete }));
      }, 4000);
      return;
    }
    // Second click: actually delete
    this.setState({ deleting: jobId, confirmDelete: null });
    try {
      const res = await adriva_deleteJob(jobId);
      if (res?.success !== false) {
        // Remove from local state immediately
        this.setState(p => ({
          deleting: null,
          jobs: (p.jobs || []).filter(j => j.job_id !== jobId),
          selectedJob: p.selectedJob?.job_id === jobId ? null : p.selectedJob,
          toasts: [...p.toasts, {
            id: `del-${Date.now()}`, title: "Job deleted", message: jobId.substring(0, 8) + "…", ok: true,
          }],
        }));
      } else {
        this.setState(p => ({
          deleting: null,
          toasts: [...p.toasts, {
            id: `del-err-${Date.now()}`, title: "Delete failed",
            message: res?.message || "Could not delete job", ok: false,
          }],
        }));
      }
    } catch (e: any) {
      this.setState(p => ({
        deleting: null,
        toasts: [...p.toasts, {
          id: `del-err-${Date.now()}`, title: "Delete error", message: e.message, ok: false,
        }],
      }));
    }
  };

  // ── Reset all entity-specific state to defaults ───────────────────────────
  private resetEntityState() {
    // Kill any running poll interval — user must re-enable on the new app/entity.
    if (this.state.refreshId) clearInterval(this.state.refreshId);

    this.prevMap = {};
    this.setState({
      selectedJob:     null,
      jobTypeFilter:   "all",
      statusFilter:    "all",
      searchText:      "",
      dqStatusFilter:  "all",
      dqSourceFilter:  "all",
      dqColumnFilter:  "all",
      dqPage:          1,
      expandedAgents:  new Set(),
      expandedChunk:   null,
      expandedThreads: new Set(),
      expandedCalls:   new Set(),
      agenticTab:      0,
      autoRefresh:     false,
      liveMode:        false,
      refreshId:       null,
    });
  }

  // Single source of truth for "get fresh data". Respects scope:
  //  · entity-scoped (onRefresh wired) → ask the parent to re-filter & push new initialJobs
  //  · standalone app monitor          → fetch all app jobs
  refreshData = (silent = false) => {
    const { onRefresh, appNm } = this.props;
    if (onRefresh) {
      if (!silent) {
        this.setState({ loading: true });
        // safety: don't leave the spinner stuck if the parent never pushes an update
        setTimeout(() => this.setState(s => (s.loading ? { loading: false } : null)), 6000);
      }
      onRefresh();
      // refresh the open detail panel too, without touching list scope
      if (this.state.selectedJob?.job_id) this.loadDetails(this.state.selectedJob.job_id);
      return;
    }
    this.load(appNm, silent);
  };

  componentDidMount() {
    const { appNm, initialJobs, initialStatusFilter, initialJobTypeFilter, initialSelectedJobId } = this.props;

    const patch: Partial<State> = {};
    if (initialStatusFilter)  patch.statusFilter  = initialStatusFilter;
    if (initialJobTypeFilter) patch.jobTypeFilter = initialJobTypeFilter;
    if (Object.keys(patch).length) this.setState(patch as any);

    if (initialJobs != null) {
      this.applyUpdate(initialJobs);
      if (initialSelectedJobId) this.loadDetails(initialSelectedJobId);
    } else if (appNm) {
      this.load(appNm);
    }
  }

  componentDidUpdate(prevProps: typeof this.props) {

    const {
      appNm, initialJobs, selectedEntityId, initialStatusFilter, initialJobTypeFilter, initialSelectedJobId,
    } = this.props;

    // ── App changed → full reload ─────────────────────────────────────────
    if (prevProps.appNm !== appNm) {
      this.resetEntityState();
      this.load(appNm);
      return;
    }

    // ── Entity switch vs. same-entity refresh ─────────────────────────────
    // Prefer selectedEntityId to tell a real entity change from a data refresh;
    // fall back to array-reference comparison when the parent doesn't pass it.
    const hasEntityKey  = prevProps.selectedEntityId !== undefined || selectedEntityId !== undefined;
    const entityChanged = hasEntityKey
      ? prevProps.selectedEntityId !== selectedEntityId
      : prevProps.initialJobs !== initialJobs;   // legacy fallback

    if (entityChanged) {
      this.resetEntityState();
      if (initialJobs != null) this.applyUpdate(initialJobs);

      const patch: Partial<State> = {};
      if (initialStatusFilter)  patch.statusFilter  = initialStatusFilter;
      if (initialJobTypeFilter) patch.jobTypeFilter = initialJobTypeFilter;
      if (Object.keys(patch).length) this.setState(patch as any);

      if (initialSelectedJobId) this.loadDetails(initialSelectedJobId);
      return;
    }

    // Same entity, parent pushed fresh data (Refresh / Auto / Live) → merge in
    // place: keeps the selected job, filters and change-highlights intact.
    if (prevProps.initialJobs !== initialJobs && initialJobs != null) {
      this.applyUpdate(initialJobs);
      if (this.state.loading) this.setState({ loading: false });
    }

    // ── Same entity, only filters changed (Dashboard KPI drill-down) ──────
    if (
      initialStatusFilter  !== prevProps.initialStatusFilter ||
      initialJobTypeFilter !== prevProps.initialJobTypeFilter
    ) {
      const patch: Partial<State> = {};
      if (initialStatusFilter)  patch.statusFilter  = initialStatusFilter;
      if (initialJobTypeFilter) patch.jobTypeFilter = initialJobTypeFilter;
      this.setState(patch as any);
    }

    if (initialSelectedJobId && initialSelectedJobId !== prevProps.initialSelectedJobId) {
      this.loadDetails(initialSelectedJobId);
    }
  }

  componentWillUnmount() {
    if (this.state.refreshId) clearInterval(this.state.refreshId);
    Object.values(this.hlT).forEach(clearTimeout);
  }

  load = (appNm: string, silent = false) => {
    if (!silent) this.setState({ loading: true });
    adriva_getJobs(appNm,
      (res:any) => { if (res.success) this.applyUpdate(res.data||[]); if (!silent) this.setState({loading:false}); },
      ()        => { if (!silent) this.setState({loading:false}); }
    );
  };

  loadDetails = (jobId: string) => {
    this.setState({ jobLoading:true, dqPage:1, expandedAgents:new Set(), expandedChunk:null });
    adriva_getJobDetails(jobId,
      (res:any) => {
        const detail = res.success ? res.data : null;
        this.setState(prev => {
          let updatedJobs = prev.jobs;
          if (detail && prev.jobs) {
            // ── Derive progress from detail data ──────────────────────────
            // The detail endpoint may not return `progress` directly (it's
            // stored on the Job row, not recomputed by the detail view).
            // Derive it from whatever data IS available in the response:
            //   1. detail.progress if present and > 0
            //   2. agentic: succChunks / totalChunks across all threads
            //   3. DQ: pass+fail / total_rules
            //   4. fall back to existing left-panel value
            const deriveProgress = (d: any, fallback: number): number => {
              // For stuck/running jobs that show progress=100 in the DB but haven't
              // actually finished: the stored progress=100 was written by the last
              // _ProgressReporter.increment() call, not by finish(). The real field
              // completion is in the message: "107/323 — entity chunk-108".
              // Parse the message to get the true done/total ratio.
              const jobIsStuck = d.possibly_stuck === true || (isRunning(d.status || "") && !!d.end_time);
              const jobIsFailed = isFail(d.status || "");

              if ((jobIsStuck || jobIsFailed) && d.message) {
                // Message format: "Label: 107/323 — entity chunk-N"
                const match = d.message.match(/(\d+)\/(\d+)/);
                if (match) {
                  const done  = parseInt(match[1], 10);
                  const total = parseInt(match[2], 10);
                  if (total > 0 && done <= total) {
                    return Math.round((done / total) * 100);
                  }
                }
              }

              // For normal running/completed jobs: use stored progress as-is
              if (d.progress != null && d.progress > 0 && !jobIsStuck) return d.progress;

              // DQ job — compute from rule counts
              if (d.total_rules != null && d.total_rules > 0) {
                const done = (d.pass_count || 0) + (d.fail_count || 0) + (d.skip_count || 0) + (d.error_count || 0);
                return Math.round((done / d.total_rules) * 100);
              }

              // Last resort: stored progress (even if 100 on a stuck job)
              if (d.progress != null) return d.progress;

              return fallback;
            };

            updatedJobs = prev.jobs.map((j: any) => {
              if (j.job_id !== detail.job_id) return j;
              const progress = deriveProgress(detail, j.progress ?? 0);
              return {
                ...j,
                status:        detail.status   ?? j.status,
                progress,
                message:       detail.message  ?? j.message,
                end_time:      detail.end_time ?? j.end_time,
                agentic_usage: detail.agentic_usage ?? j.agentic_usage,
                summary: detail.total_rules != null ? {
                  ...j.summary,
                  total_rules: detail.total_rules,
                  pass_count:  detail.pass_count,
                  fail_count:  detail.fail_count,
                  skip_count:  detail.skip_count,
                  error_count: detail.error_count,
                } : j.summary,
              };
            });

            const merged = updatedJobs.find((j: any) => j.job_id === detail.job_id);
            if (merged) this.prevMap[detail.job_id] = merged;
          }
          return {
            selectedJob:    detail,
            jobLoading:     false,
            dqStatusFilter: "all",
            dqSourceFilter: "all",
            dqColumnFilter: "all",
            jobs:           updatedJobs,
          };
        });
      },
      () => this.setState({ jobLoading:false })
    );
  };

  applyUpdate = (jobs: any[]) => {
    const newMap: Record<string,any> = {};
    jobs.forEach(j => (newMap[j.job_id] = j));
    const changedMap = {...this.state.changedMap};
    const toasts     = [...this.state.toasts];
    Object.values(newMap).forEach((job:any) => {
      const prev = this.prevMap[job.job_id];
      const ps   = (prev?.status??"").toLowerCase();
      const ns   = (job.status??"").toLowerCase();
      const prgChg = prev && Math.abs((job.progress??0)-(prev.progress??0)) >= 5;
      if (prev && (ns !== ps || prgChg)) {
        changedMap[job.job_id] = Date.now();
        if (this.hlT[job.job_id]) clearTimeout(this.hlT[job.job_id]);
        this.hlT[job.job_id] = setTimeout(() => {
          this.setState(s => { const c = {...s.changedMap}; delete c[job.job_id]; return {changedMap:c}; });
        }, 3000);
      }
      if (prev && isRunning(ps) && isSuccess(ns))
        toasts.push({id:`${job.job_id}-${Date.now()}`,title:"Job completed",message:job.job_type||job.job_id,ok:true});
      if (prev && isFail(ns) && ns !== ps)
        toasts.push({id:`${job.job_id}-${Date.now()}`,title:"Job failed",message:job.job_type||job.job_id,ok:false});
    });
    this.prevMap = newMap;
    this.setState({jobs, changedMap, toasts});
  };

  toggleAR = () => this.setState(p => {
    const on = !p.autoRefresh;
    if (p.refreshId) clearInterval(p.refreshId);
    if (on) {
      //const id = setInterval(() => { if (this.state.autoRefresh) this.load(this.props.appNm, true); }, 5000);
      const id = setInterval(() => { if (this.state.autoRefresh) this.refreshData(true); }, 5000);
      return { autoRefresh:true, refreshId:id };
    }
    return { autoRefresh:false, refreshId:null };
  });

  toggleLive = () => this.setState(p => {
    const on = !p.liveMode;
    // Turning Live on: start interval if not already running
    if (on) {
      if (p.refreshId) {
        // interval already running (Auto was on) — just flip the flag
        return { liveMode: true, autoRefresh: true, refreshId: p.refreshId };
      }
      const id = setInterval(() => { if (this.state.autoRefresh || this.state.liveMode) this.refreshData(true); }, 5000);
      //const id = setInterval(() => { if (this.state.autoRefresh || this.state.liveMode) this.load(this.props.appNm, true); }, 5000);
      return { liveMode: true, autoRefresh: true, refreshId: id };
    }
    // Turning Live off: stop everything
    if (p.refreshId) clearInterval(p.refreshId);
    return { liveMode: false, autoRefresh: false, refreshId: null };
  });
  toggleField = (jid:string, f:string) => this.setState(p => {
    const s = new Set(p.expandedFields[jid]||[]);
    s.has(f) ? s.delete(f) : s.add(f);
    return {expandedFields:{...p.expandedFields,[jid]:s}};
  });
  toggleAgent = (k:string) => this.setState(p => { const s=new Set(p.expandedAgents); s.has(k)?s.delete(k):s.add(k); return {expandedAgents:s}; });
  toggleChunk = (k:string) => this.setState(p => ({expandedChunk: p.expandedChunk===k ? null : k}));
  setFilter   = (k:string, v:string) => this.setState({[k]: k==="dqPageSize" ? +v : v} as any);
  setPage     = (n:number) => this.setState({dqPage:n});

  filteredJobs() {
    const {jobs, jobTypeFilter, statusFilter, searchText} = this.state;
    if (!jobs) return [];
    const q = searchText.trim().toLowerCase();
    return jobs.filter(j => {
      if (jobTypeFilter !== "all" && (j.job_type||"").toLowerCase() !== jobTypeFilter.toLowerCase()) return false;
      if (statusFilter  !== "all" && (j.status  ||"").toLowerCase() !== statusFilter.toLowerCase())  return false;
      if (q && !(j.job_type||"").toLowerCase().includes(q) && !(j.job_id||"").toLowerCase().includes(q)) return false;
      return true;
    });
  }

  render() {
    const { selectedJob, loading, jobLoading, changedMap, toasts, liveMode, autoRefresh } = this.state;
    const { appNm, entityName } = this.props;
    const fJobs    = this.filteredJobs();
    const allJobs  = this.state.jobs || [];
    const jobTypes = distinct(allJobs.map(j => j.job_type||""));
    const statuses = distinct(allJobs.map(j => j.status||""));

    return (
      <div className="jm">
        <style>{CSS}</style>

        {/* ── Top bar ── */}
        <div className="jm-topbar">
          <span className="jm-topbar-title">Job Monitor</span>

          {/* Entity name pill — NEW */}
          {entityName && (
            <>
              <span className="jm-topbar-sep">›</span>
              <span className="jm-topbar-entity" title={entityName}>{entityName}</span>
            </>
          )}

          {/* App name — secondary */}
          {appNm && <span className="jm-topbar-app">{appNm}</span>}

          <div className="jm-spacer" />
          <button className={`jm-btn-top${liveMode?" on":""}`} onClick={this.toggleLive}>
            <span style={{width:6,height:6,borderRadius:"50%",background:liveMode?"#4ade80":T.brandMid,display:"inline-block"}} /> Live
          </button>
          <button className={`jm-btn-top${autoRefresh?" on":""}`} onClick={this.toggleAR}>⟳ Auto</button>
          <button className="jm-btn-top" onClick={() => this.refreshData(false)} disabled={loading}>
            {loading?<div className="jm-ring" style={{width:12,height:12,borderTopColor:"#fff"}}/>:"↻"} Refresh
          </button>
        </div>

        {/* ── Filters ── */}
        <div className="jm-filters">
          <input className="jm-inp" placeholder="Search jobs…" value={this.state.searchText}
            onChange={e => this.setState({searchText:e.target.value})} />
          <select className="jm-sel" value={this.state.jobTypeFilter} onChange={e => this.setState({jobTypeFilter:e.target.value})}>
            <option value="all">All types</option>
            {jobTypes.map(jt => <option key={jt} value={jt}>{jt}</option>)}
          </select>
          <select className="jm-sel" value={this.state.statusFilter} onChange={e => this.setState({statusFilter:e.target.value})}>
            <option value="all">All statuses</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="jm-count">{fJobs.length} / {allJobs.length} jobs</span>
        </div>

        {/* ── Body ── */}
        <div className="jm-body">
          {/* Left panel */}
          <div className="jm-left">
            <div className="jm-left-hdr">
              <span>{fJobs.length} runs</span>
              {liveMode && <span style={{color:T.green,fontSize:9}}>● LIVE</span>}
            </div>
            {/* Resume banner — shown when stuck/failed jobs exist, triggers directly */}
            {/*(() => {
              const stuckOrFailed = allJobs.filter(j => {
                const s = (j.status||"").toLowerCase();
                return isFail(s) || (isRunning(s) && !!j.end_time);
              });
              if (!stuckOrFailed.length) return null;
              // Pick the most recent one as the resume target
              const target = [...stuckOrFailed].sort(
                (a,b) => new Date(b.start_time||0).getTime() - new Date(a.start_time||0).getTime()
              )[0];
              const isResuming = this.state.resuming === target.job_id;
              const targetStatus = (target.status||"").toLowerCase();
              const isStuck = isRunning(targetStatus) && !!target.end_time;
              return (
                <div style={{
                  padding:"8px 12px", background:"#fffbeb", borderBottom:"1px solid #fde68a",
                  display:"flex", alignItems:"center", gap:8, fontSize:11, flexShrink:0,
                }}>
                  <span style={{fontSize:13}}>{isStuck ? "⚠️" : "❌"}</span>
                  <span style={{flex:1,color:"#92400e",fontWeight:500}}>
                    {stuckOrFailed.length} job{stuckOrFailed.length>1?"s":""} need{stuckOrFailed.length===1?"s":""} attention
                  </span>
                  <button
                    style={{
                      all:"unset", cursor: isResuming ? "not-allowed" : "pointer",
                      fontSize:11, fontWeight:700, color:"#0f766e",
                      background:"#f0fdfa", padding:"3px 10px",
                      borderRadius:5, border:"1px solid #5eead4",
                      opacity: isResuming ? 0.6 : 1,
                      display:"flex", alignItems:"center", gap:5,
                    }}
                    onClick={() => !isResuming && this.triggerResume(target)}
                  >
                    {isResuming
                      ? <><div className="jm-ring" style={{width:10,height:10,borderTopColor:"#0f766e",borderColor:"#ccfbf1"}}/>Resuming…</>
                      : "⏭ Resume"
                    }
                  </button>
                </div>
              );
            })()*/}
            <div className="jm-list">
              {loading && !this.state.jobs
                ? <div style={{display:"flex",justifyContent:"center",padding:32}}><div className="jm-ring"/></div>
                : !fJobs.length
                  ? <div style={{padding:24,textAlign:"center",color:T.textMuted,fontSize:12}}>No jobs found.</div>
                  : fJobs.map((job:any) => {
                      const s   = job.status||"";
                      const m   = statusMeta(s, job.possibly_stuck);
                      const sel = selectedJob?.job_id === job.job_id;
                      const chg = !!changedMap[job.job_id];
                      const hasBadStat = job.config?.agent_stat && (
                        (job.config.agent_stat.total_chunks>0 && job.config.agent_stat.succeeded_chunks===0) ||
                        job.config.agent_stat.partial_result
                      );
                      return (
                        <div key={job.job_id} className={`jm-row${sel?" sel":""}${chg?" chg":""}`} onClick={()=>this.loadDetails(job.job_id)}>
                          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                            <Dot s={s} />
                            <span style={{fontWeight:600,fontSize:12.5,color:T.textPrimary,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{job.job_type||job.job_id}</span>
                            <Pill s={s} possiblyStuck={job.possibly_stuck} style={{fontSize:9.5,flexShrink:0}} />
                          </div>
                          <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:10,color:T.textMuted,marginBottom:4}}>
                            {fmt.date(job.start_time)}{job.end_time?` → ${fmt.time(job.end_time)}`:""}
                            {fmt.dur(job.start_time,job.end_time)&&` (${fmt.dur(job.start_time,job.end_time)})`}
                          </div>
                          <div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
                            <TypeBadge jt={job.job_type||""} />
                            {job.summary?.total_rules!=null && <>
                              <span style={{fontSize:10,color:T.textMuted}}>📋 {job.summary.total_rules}</span>
                              <span style={{fontSize:10,color:T.green}}>✓ {job.summary.pass_count}</span>
                              <span style={{fontSize:10,color:T.red}}>✗ {job.summary.fail_count}</span>
                            </>}
                            {hasBadStat && (() => {
                              const st  = job.config.agent_stat;
                              const as_ = st.total_chunks>0&&st.succeeded_chunks===0?"fail":"partial";
                              const am  = statusMeta(as_);
                              return <span className="jm-pill" style={{background:am.bg,color:am.text,borderColor:am.border,fontSize:9.5}}>
                                {as_==="partial"?`◑ ${st.failed_chunks_count} chunks`:"✕ agent fail"}
                              </span>;
                            })()}
                            {/* Delete button — shown on hover via CSS, confirmed on second click */}
                            <button
                              className="jm-delete-btn"
                              disabled={this.state.deleting === job.job_id}
                              onClick={e => { e.stopPropagation(); this.deleteJob(job.job_id); }}
                              title={this.state.confirmDelete === job.job_id ? "Click again to confirm delete" : "Delete this job"}
                              style={{
                                all:"unset", cursor:"pointer", marginLeft:"auto",
                                fontSize:10, padding:"1px 6px", borderRadius:4,
                                border:`1px solid ${this.state.confirmDelete === job.job_id ? T.redBorder : T.border}`,
                                background: this.state.confirmDelete === job.job_id ? T.redBg : "transparent",
                                color: this.state.confirmDelete === job.job_id ? T.red : T.textMuted,
                                fontWeight: this.state.confirmDelete === job.job_id ? 700 : 400,
                                opacity: this.state.deleting === job.job_id ? 0.5 : 1,
                                transition:"all .12s",
                              }}
                            >
                              {this.state.deleting === job.job_id
                                ? "…"
                                : this.state.confirmDelete === job.job_id
                                  ? "✕ Confirm"
                                  : "✕"
                              }
                            </button>
                          </div>
                          {job.progress!=null && <>
                            <div className="jm-prog">
                              <div className="jm-prog-fill" style={{width:`${job.progress}%`,background:isFail(s)?T.red:isSuccess(s)?T.green:T.brand}}/>
                            </div>
                            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:9.5,color:T.textMuted,marginTop:2}}>{job.progress}%</div>
                          </>}
                        </div>
                      );
                    })
              }
            </div>
          </div>

          {/* Right panel */}
          <div className="jm-right">
            {!selectedJob && !jobLoading ? (
              <div className="jm-empty">
                <div style={{fontSize:32,opacity:.3}}>⬡</div>
                <div style={{fontSize:14,fontWeight:600,color:T.textLabel}}>Select a job</div>
                <div style={{fontSize:12}}>Click any row on the left to inspect details.</div>
              </div>
            ) : (
              <>
                <div className="jm-detail-top">
                  <div style={{minWidth:0,flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:5,flexWrap:"wrap"}}>
                      {selectedJob && <TypeBadge jt={selectedJob.job_type||""} />}
                      {selectedJob && <Pill s={selectedJob.status||""} possiblyStuck={selectedJob.possibly_stuck} />}
                    </div>
                    <div style={{fontWeight:700,fontSize:15,color:T.textPrimary,marginBottom:3}}>{selectedJob?.job_type||selectedJob?.job_id}</div>
                    <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:10.5,color:T.textMuted}}>
                      {selectedJob?.job_id}
                      {selectedJob?.start_time && ` · ${fmt.date(selectedJob.start_time)}`}
                      {selectedJob?.end_time   && ` → ${fmt.time(selectedJob.end_time)}`}
                      {selectedJob?.start_time && fmt.dur(selectedJob.start_time,selectedJob.end_time) && ` · ${fmt.dur(selectedJob.start_time,selectedJob.end_time)}`}
                    </div>
                    {selectedJob?.message && (
                      <div style={{marginTop:4,fontSize:11,color:isFail(selectedJob.status)?T.red:T.textMuted}}>{selectedJob.message}</div>
                    )}
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0,flexWrap:"wrap"}}>
                    {/* ★ Stop Pipeline — shown for running, failed, stuck, or cancelled */}
                    {selectedJob && (() => {
                      const s = (selectedJob.status||"").toLowerCase();
                      const stuck = selectedJob.possibly_stuck === true;
                      const showStop = isRunning(s) || stuck;
                      if (!showStop) return null;
                      const isCancelling = this.state.cancelling === selectedJob.job_id;
                      return (
                        <button
                          className="jm-btn"
                          style={{
                            background: isCancelling ? T.amberBg : "#fff",
                            borderColor: T.amberBorder,
                            color: T.amber,
                            fontWeight: 600,
                            opacity: isCancelling ? 0.7 : 1,
                          }}
                          disabled={isCancelling}
                          onClick={() => this.triggerCancel(selectedJob)}
                          title="Stop the active pipeline for this entity — marks running jobs as cancelled"
                        >
                          {isCancelling
                            ? <><div className="jm-ring" style={{width:11,height:11,borderTopColor:T.amber,borderColor:T.amberBg,marginRight:4}}/>Stopping…</>
                            : <>⏹ Stop Pipeline</>
                          }
                        </button>
                      );
                    })()}

                    {/* Resume — shown for stuck, failed, or cancelled jobs */}
                    {selectedJob && (() => {
                      const s = (selectedJob.status||"").toLowerCase();
                      const stuck = selectedJob.possibly_stuck === true;
                      const resumable = stuck || isFail(s) || s === "cancelled";
                      if (!resumable) return null;
                      const isResuming = this.state.resuming === selectedJob.job_id;
                      const label = stuck ? "Stuck — Resume"
                        : s === "cancelled" ? "Resume"
                        : "Failed — Resume";
                      return (
                        <button
                          className="jm-btn"
                          style={{
                            background: "#f0fdfa",
                            borderColor:"#5eead4", color:"#0f766e", fontWeight:600,
                            opacity: isResuming ? 0.7 : 1,
                          }}
                          disabled={isResuming}
                          onClick={() => this.triggerResume(selectedJob)}
                          title="Resume from where it stopped — skips already-done jobs"
                        >
                          {isResuming
                            ? <><div className="jm-ring" style={{width:11,height:11,borderTopColor:"#0f766e",borderColor:"#ccfbf1",marginRight:4}}/>Resuming…</>
                            : <>⏭ {label}</>
                          }
                        </button>
                      );
                    })()}

                    <button className="jm-btn" onClick={()=>this.setState({selectedJob:null})}>✕ Close</button>
                  </div>
                </div>

                {jobLoading
                  ? <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
                      <div className="jm-ring"/><span style={{fontSize:12,color:T.textMuted}}>Loading…</span>
                    </div>
                  : selectedJob && (
                    <DetailBody
                      job={selectedJob} st={this.state}
                      appNm={appNm}
                      onResumed={() => this.load(appNm, true)}
                      onToggleField={this.toggleField}
                      onToggleAgent={this.toggleAgent}
                      onToggleChunk={this.toggleChunk}
                      onFilter={this.setFilter}
                      onPage={this.setPage}
                      onToggleThread={this.toggleThread}
                      onToggleCall={this.toggleCall}
                      onSetAgenticTab={this.setAgenticTab}
                    />
                  )
                }
              </>
            )}
          </div>
        </div>

        {/* Toasts */}
        <div className="jm-toasts">
          {toasts.map(t => (
            <div key={t.id} className={`jm-toast${t.ok?" ok":" err"}`}>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,fontSize:12,marginBottom:1}}>{t.title}</div>
                <div style={{fontSize:11,opacity:.85}}>{t.message}</div>
              </div>
              <button
                style={{all:"unset",cursor:"pointer",fontSize:16,opacity:.6,padding:"0 2px"}}
                onClick={()=>this.setState(p=>({toasts:p.toasts.filter(x=>x.id!==t.id)}))}
              >×</button>
            </div>
          ))}
        </div>
      </div>
    );
  }
}

// ─── Types & helpers used by agentic detail panels ────────────────────────────
type LLMCall = {
  id: number; chunk_index: number; chunk_size: number;
  input_preview: string; output_preview: string;
  prompt_tokens: number; completion_tokens: number; total_tokens: number;
  cost_usd: number; status: string; error_message: string | null; created_at: string;
};
type InnerAgent = {
  agent_name: string; status: string;
  start_time: string | null; end_time: string | null;
  aggregate_usage: { prompt_tokens:number; completion_tokens:number; total_tokens:number; cost_usd:number };
  usage: LLMCall[];
};
type AgenticProcessFull = {
  id: number; name: string; status: string;
  start_time: string | null; end_time: string | null;
  agent_process_id: string;
  agent_process_info: {
    agents: InnerAgent[];
    aggregate_usage: { prompt_tokens:number; completion_tokens:number; total_tokens:number; cost_usd:number };
  };
  aggregate_usage: { prompt_tokens:number; completion_tokens:number; total_tokens:number; cost_usd:number };
  agent_stat: AgentStat | null;
};

type RetryStats = {
  requested?: number;
  first_pass_missing?: number;
  retry_triggered?: boolean;
  retry_attempts?: number;
  recovered_on_retry?: number;
  still_missing?: number;
  outcome?: string;
};

// retry_stats may sit at the top level of the process (get_full_tree promotes it,
// like agent_stat) OR under agent_process_info (raw merge). Look in both.
function getRetryStats(p: any): RetryStats | null {
  return p?.retry_stats ?? p?.agent_process_info?.retry_stats ?? null;
}

function isHealed(p: any): boolean {
  const rs = getRetryStats(p);
  return !!rs && (rs.still_missing ?? 1) === 0 && (rs.recovered_on_retry ?? 0) > 0;
}

function fmtTok(n?: number | null): string {
  if (n == null) return "—";
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}
function isAgenticJob(job: any): boolean { return (job.agentic_processes?.length > 0); }
function isDQJob(job: any): boolean {
  const jt = (job.job_type || "").toLowerCase();
  return jt.includes("dq") || jt.includes("rule");
}

// ─── LLM Call card ────────────────────────────────────────────────────────────
function LLMCallCard({ call, threadIdx, open, onToggle }: { call: LLMCall; threadIdx: number; open: boolean; onToggle: () => void; }) {
  return (
    <div className="ag-call">
      <div className="ag-call-hdr" onClick={onToggle}>
        <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:600, color:T.brand }}>Chunk #{call.chunk_index}</span>
        <Pill s={call.status || "success"} style={{ fontSize:9.5 }} />
        <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:T.textMuted }}>
          ↑{fmtTok(call.prompt_tokens)} ↓{fmtTok(call.completion_tokens)} = {fmtTok(call.total_tokens)} tok
        </span>
        <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:T.textMuted, marginLeft:"auto" }}>{fmt.time(call.created_at)}</span>
        <span style={{ fontSize:11, color:T.textMuted }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div className="ag-call-body">
          <div className="ag-warn">
            <span style={{ fontSize:14, flexShrink:0 }}>⚠</span>
            <span>Prompt content shown for <strong>compliance and audit transparency</strong>. This data was sent to the LLM endpoint and is subject to your data handling policy.</span>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:T.textMuted, marginBottom:4 }}>
            <span>↑ Prompt sent to LLM</span>
            <span style={{ fontFamily:"'IBM Plex Mono',monospace" }}>{fmtTok(call.prompt_tokens)} tokens</span>
          </div>
          <div className="ag-prompt">{call.input_preview || "(no preview available)"}</div>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:T.textMuted, margin:"10px 0 4px" }}>
            <span>↓ LLM response received</span>
            <span style={{ fontFamily:"'IBM Plex Mono',monospace" }}>{fmtTok(call.completion_tokens)} tokens</span>
          </div>
          <div className="ag-output">{call.output_preview || "(no preview available)"}</div>
        </div>
      )}
    </div>
  );
}

// ─── Inner agent row ──────────────────────────────────────────────────────────
function InnerAgentRow({ agent, agentKey, expandedAgents, expandedCalls, onToggleAgent, onToggleCall }: {
  agent: InnerAgent; agentKey: string;
  expandedAgents: Set<string>; expandedCalls: Set<string>;
  onToggleAgent: (k: string) => void; onToggleCall: (k: string) => void;
}) {
  const open  = expandedAgents.has(agentKey);
  const calls = agent.usage || [];
  const tokens = agent.aggregate_usage?.total_tokens ?? 0;
  if (!calls.length) return null;
  return (
    <div className="ag-agent">
      <div className="ag-agent-hdr" onClick={() => onToggleAgent(agentKey)}>
        <span style={{ width:6, height:6, borderRadius:"50%", background:T.purple, flexShrink:0, display:"inline-block" }} />
        <span style={{ fontWeight:600, fontSize:12, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{agent.agent_name}</span>
        <Pill s={agent.status} style={{ fontSize:9.5 }} />
        <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:T.textMuted }}>{calls.length} call{calls.length !== 1 ? "s" : ""} · {fmtTok(tokens)} tok</span>
        <span style={{ fontSize:11, color:T.textMuted }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div className="ag-agent-body">
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:10 }}>
            {agent.start_time && <span style={{ fontSize:11, color:T.textMuted }}>Start: <b style={{color:T.textLabel}}>{fmt.time(agent.start_time)}</b></span>}
            {agent.end_time   && <span style={{ fontSize:11, color:T.textMuted }}>End: <b style={{color:T.textLabel}}>{fmt.time(agent.end_time)}</b></span>}
          </div>
          {calls.map((call, ci) => {
            const ckey = `${agentKey}c${ci}`;
            return <LLMCallCard key={ckey} call={call} threadIdx={ci} open={expandedCalls.has(ckey)} onToggle={() => onToggleCall(ckey)} />;
          })}
        </div>
      )}
    </div>
  );
}

// ─── Thread card ──────────────────────────────────────────────────────────────
function ThreadCard({ process, threadIdx, expandedThreads, expandedAgents, expandedCalls, onToggleThread, onToggleAgent, onToggleCall }: {
  process: AgenticProcessFull; threadIdx: number;
  expandedThreads: Set<string>; expandedAgents: Set<string>; expandedCalls: Set<string>;
  onToggleThread: (k: string) => void; onToggleAgent: (k: string) => void; onToggleCall: (k: string) => void;
}) {
  const tkey   = `t${threadIdx}`;
  const open   = expandedThreads.has(tkey);
  const st     = process.agent_stat;
  const rs     = getRetryStats(process);
  const agents = (process.agent_process_info?.agents || []).filter(a => (a.usage?.length || 0) > 0);
  const tokens = process.aggregate_usage?.total_tokens ?? 0;
  return (
    <div className="ag-thread">
      <div className="ag-thread-hdr" onClick={() => onToggleThread(tkey)}>
        <div style={{ width:20, height:20, borderRadius:"50%", background:T.brandLight, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:T.brand }}>
          {threadIdx + 1}
        </div>
        <span style={{ fontWeight:600, fontSize:12.5, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{process.name}</span>
        <Pill s={process.status} style={{ fontSize:9.5 }} />
        {rs && <RetryChip rs={rs} />}  
        <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:T.textMuted }}>{fmtTok(tokens)} tok</span>
        {st?.elapsed_sec != null && <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:T.textMuted }}>{st.elapsed_sec}s</span>}
        <span style={{ fontSize:11, color:T.textMuted }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div className="ag-thread-body">
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,minmax(0,1fr))", gap:8, marginBottom:12 }}>
            {[
              { v: st?.total_chunks ?? 0,     l: "chunks",    c: T.brand  },
              { v: st?.succeeded_chunks ?? 0, l: "succeeded", c: T.green  },
              { v: fmtTok(tokens),            l: "tokens",    c: T.purple },
              { v: st?.elapsed_sec != null ? `${st.elapsed_sec}s` : "—", l: "elapsed", c: T.textPrimary },
            ].map((k, i) => (
              <div className="jm-kpi" key={i}>
                <div className="jm-kpi-v" style={{ color:k.c, fontSize:16 }}>{k.v}</div>
                <div className="jm-kpi-l">{k.l}</div>
              </div>
            ))}
          </div>
          {st && (st.failed_chunks_count ?? 0) > 0 && !isHealed(process) && (
            <AgentStatPanel stat={st} expandedChunk={null} onToggle={() => {}} />
          )}
          {agents.length > 0 && (
            <>
              <div className="jm-sec-title" style={{ marginBottom:8 }}>LLM calls</div>
              {agents.map((agent, ai) => {
                const akey = `${tkey}a${ai}`;
                return <InnerAgentRow key={akey} agent={agent} agentKey={akey} expandedAgents={expandedAgents} expandedCalls={expandedCalls} onToggleAgent={onToggleAgent} onToggleCall={onToggleCall} />;
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Usage breakdown ──────────────────────────────────────────────────────────
function UsageBreakdown({ processes }: { processes: AgenticProcessFull[] }) {
  const allCalls: Array<{thread:number;agent:string;chunk:number;prompt:number;completion:number;total:number;status:string;time:string}> = [];
  processes.forEach((p, pi) => {
    (p.agent_process_info?.agents || []).forEach(a => {
      (a.usage || []).forEach(c => allCalls.push({ thread:pi+1, agent:a.agent_name, chunk:c.chunk_index, prompt:c.prompt_tokens, completion:c.completion_tokens, total:c.total_tokens, status:c.status||"success", time:c.created_at }));
    });
  });
  allCalls.sort((a,b) => new Date(a.time).getTime() - new Date(b.time).getTime());
  const totPr = allCalls.reduce((s,c)=>s+(c.prompt||0),0);
  const totCo = allCalls.reduce((s,c)=>s+(c.completion||0),0);
  const totTo = allCalls.reduce((s,c)=>s+(c.total||0),0);
  const ratio = totTo > 0 ? Math.round((totPr/totTo)*100) : 0;
  return (
    <>
      <div className="jm-kpis" style={{ marginBottom:12 }}>
        {[ {v:allCalls.length,l:"LLM calls",c:T.brand}, {v:fmtTok(totPr),l:"prompt tokens",c:T.purple}, {v:fmtTok(totCo),l:"completion tokens",c:T.purple}, {v:fmtTok(totTo),l:"total tokens",c:T.brand} ].map((k,i)=>(
          <div className="jm-kpi" key={i}><div className="jm-kpi-v" style={{color:k.c}}>{k.v}</div><div className="jm-kpi-l">{k.l}</div></div>
        ))}
      </div>
      <div style={{ padding:"10px 14px", border:`1px solid ${T.border}`, borderRadius:8, background:T.bgSidebar, marginBottom:14 }}>
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:T.textMuted, marginBottom:4 }}>
          <span>Prompt vs completion split</span>
          <span style={{ fontFamily:"'IBM Plex Mono',monospace" }}>{ratio}% / {100-ratio}%</span>
        </div>
        <div style={{ height:6, borderRadius:99, overflow:"hidden", display:"flex" }}>
          <div style={{ width:`${ratio}%`, background:T.purple, height:"100%" }} />
          <div style={{ flex:1, background:T.brand, height:"100%", opacity:.4 }} />
        </div>
        <div style={{ display:"flex", gap:16, marginTop:5, fontSize:10, color:T.textMuted }}>
          <span style={{ display:"flex", alignItems:"center", gap:4 }}><span style={{ width:8, height:8, borderRadius:"50%", background:T.purple, display:"inline-block" }} /> Prompt</span>
          <span style={{ display:"flex", alignItems:"center", gap:4 }}><span style={{ width:8, height:8, borderRadius:"50%", background:T.brand, opacity:.6, display:"inline-block" }} /> Completion</span>
        </div>
      </div>
      <div className="jm-sec-title">All LLM calls — chronological</div>
      <div style={{ overflowX:"auto" }}>
        <table className="ag-tbl">
          <thead><tr><th>Thread</th><th>Agent</th><th>Chunk</th><th>Prompt</th><th>Completion</th><th>Total</th><th>Status</th><th>Time</th></tr></thead>
          <tbody>
            {allCalls.map((c,i)=>(
              <tr key={i}>
                <td><span style={{ background:T.brandLight, color:T.brand, padding:"1px 7px", borderRadius:99, fontSize:10, fontWeight:600 }}>#{c.thread}</span></td>
                <td style={{ fontSize:11, color:T.textMuted, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.agent}</td>
                <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11 }}>#{c.chunk}</td>
                <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:T.purple }}>{fmtTok(c.prompt)}</td>
                <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:T.brand }}>{fmtTok(c.completion)}</td>
                <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:600 }}>{fmtTok(c.total)}</td>
                <td><Pill s={c.status} style={{ fontSize:9.5 }} /></td>
                <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:T.textMuted }}>{fmt.time(c.time)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Agentic overview ─────────────────────────────────────────────────────────
function AgenticOverview({ job }: { job: any }) {
  const ap: AgenticProcessFull[] = job.agentic_processes || [];
  const au = job.agentic_usage || {};
  //const totalChunks = ap.reduce((s,p)=>s+(p.agent_stat?.total_chunks||0),0);
  //const succChunks  = ap.reduce((s,p)=>s+(p.agent_stat?.succeeded_chunks||0),0);
  const totalChunks = ap.reduce((s,p)=>s+(p.agent_stat?.total_chunks||0),0);
  const succChunks  = ap.reduce((s,p)=>{
    const st = p.agent_stat;
    if (!st) return s;
    // retry recovered everything → count all chunks as succeeded
    return s + (isHealed(p) ? (st.total_chunks||0) : (st.succeeded_chunks||0));
  }, 0);

  const totalCalls  = ap.reduce((s,p)=>s+(p.agent_process_info?.agents||[]).reduce((ss,a)=>ss+(a.usage?.length||0),0),0);
  const pct      = totalChunks > 0 ? Math.round((succChunks/totalChunks)*100) : 100;
  const barColor = pct === 100 ? T.green : pct >= 50 ? T.amber : T.red;
  return (
    <>
      <div className="ag-notice">
        <span style={{ fontSize:15, flexShrink:0 }}>ⓘ</span>
        <span>This is an <strong>agentic enrichment job</strong> — it runs LLM agents to classify fields, not data quality rules. DQ rule counts are not applicable for this job type.</span>
      </div>
      <div className="jm-kpis" style={{ marginBottom:14 }}>
        {[ {v:ap.length,l:"parallel threads",c:T.brand}, {v:totalCalls,l:"LLM calls",c:T.brand}, {v:totalChunks,l:"total chunks",c:T.brand}, {v:fmt.dur(job.start_time,job.end_time)||"—",l:"wall time",c:T.green} ].map((k,i)=>(
          <div className="jm-kpi" key={i}><div className="jm-kpi-v" style={{color:k.c}}>{k.v}</div><div className="jm-kpi-l">{k.l}</div></div>
        ))}
      </div>
      <div className="jm-sec-title">LLM usage — all threads combined</div>
      <div className="jm-kpis" style={{ marginBottom:14 }}>
        {[ {v:fmtTok(au.total_tokens),l:"total tokens",c:T.brand}, {v:fmtTok(au.prompt_tokens),l:"prompt tokens",c:T.purple}, {v:fmtTok(au.completion_tokens),l:"completion tokens",c:T.purple}, {v:au.cost_usd!=null?`$${au.cost_usd.toFixed(4)}`:"—",l:"cost",c:T.green} ].map((k,i)=>(
          <div className="jm-kpi" key={i}><div className="jm-kpi-v" style={{color:k.c}}>{k.v}</div><div className="jm-kpi-l">{k.l}</div></div>
        ))}
      </div>
      <div style={{ padding:"10px 14px", border:`1px solid ${T.border}`, borderRadius:8, background:T.bgSidebar, marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:T.textMuted, marginBottom:4 }}>
          <span>Chunk success rate across all threads</span>
          <span style={{ fontWeight:600, color:barColor, fontFamily:"'IBM Plex Mono',monospace" }}>{pct}% ({succChunks}/{totalChunks})</span>
        </div>
        <div style={{ height:5, borderRadius:99, background:T.border, overflow:"hidden" }}>
          <div style={{ height:"100%", width:`${pct}%`, background:barColor, borderRadius:99, transition:"width .4s" }} />
        </div>
      </div>
      <div className="jm-sec-title">Per-thread summary</div>
      <div style={{ overflowX:"auto", border:`1px solid ${T.border}`, borderRadius:8, marginBottom:4 }}>
        <table className="ag-tbl">
          <thead><tr><th>#</th><th>Thread ID</th><th>Status</th><th>Chunks</th><th>Tokens</th><th>Elapsed</th><th>Error</th></tr></thead>
          <tbody>
            {ap.map((p,i)=>{
              const st = p.agent_stat;
              //const isFailed = (p.status||"").toLowerCase() === "failed" ||
              //  (st && st.total_chunks === 0 && st.succeeded_chunks === 0);
              const isFailed = !isHealed(p) && (
                (p.status||"").toLowerCase() === "failed" ||
                (st && st.total_chunks === 0 && st.succeeded_chunks === 0)
              );
              // Extract error from failed_chunks or agent_process_info
              const failedChunks = st?.failed_chunks || [];
              const errorText = failedChunks.length > 0
                ? failedChunks[0].error
                : (p.agent_process_info as any)?.error || null;
              return (
                <tr key={p.id} style={isFailed ? {background:T.redBg} : undefined}>
                  <td><span style={{ background: isFailed ? T.redBg : T.brandLight, color: isFailed ? T.red : T.brand, padding:"1px 7px", borderRadius:99, fontSize:10, fontWeight:600 }}>#{i+1}</span></td>
                  <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:T.textMuted }}>{(p.agent_process_id||"").substring(0,8)}…</td>
                  <td><Pill s={isFailed ? "failed" : p.status} style={{ fontSize:9.5 }} /></td>
                  <td>
                    <span style={{color: st?.succeeded_chunks ? T.green : T.red}}>{st?.succeeded_chunks??0}</span>
                    <span style={{color:T.textMuted}}>/{st?.total_chunks??0}</span>
                    {isFailed && (st?.failed_chunks_count ?? 0) > 0 && (
                      <span style={{marginLeft:4,fontSize:9.5,color:T.red}}>({st?.failed_chunks_count} failed)</span>
                    )}
                  </td>
                  <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11 }}>{fmtTok(p.aggregate_usage?.total_tokens)}</td>
                  <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:T.textMuted }}>{st?.elapsed_sec!=null?`${st.elapsed_sec}s`:"—"}</td>
                  <td style={{ fontSize:10.5, color:T.red, maxWidth:200 }}>
                    {errorText
                      ? <span title={errorText} style={{overflow:"hidden",textOverflow:"ellipsis",display:"block",whiteSpace:"nowrap"}}>{errorText}</span>
                      : isFailed ? <span style={{color:T.red,fontStyle:"italic"}}>Empty response from agentic service</span>
                      : "—"
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Agentic detail panel (tabbed) ────────────────────────────────────────────
function AgenticDetailPanel({ job, expandedThreads, expandedAgents, expandedCalls, agenticTab, onToggleThread, onToggleAgent, onToggleCall, onSetTab }: {
  job: any; expandedThreads: Set<string>; expandedAgents: Set<string>; expandedCalls: Set<string>; agenticTab: number;
  onToggleThread: (k:string)=>void; onToggleAgent: (k:string)=>void; onToggleCall: (k:string)=>void; onSetTab: (n:number)=>void;
}) {
  const ap: AgenticProcessFull[] = job.agentic_processes || [];
  const tabs = ["Overview", `Parallel threads (${ap.length})`, "LLM usage & calls"];
  return (
    <div className="jm-sec">
      <div className="ag-tabs">
        {tabs.map((t,i)=>(
          <button key={i} className={`ag-tab${agenticTab===i?" active":""}`} onClick={()=>onSetTab(i)}>{t}</button>
        ))}
      </div>
      {agenticTab === 0 && <AgenticOverview job={job} />}
      {agenticTab === 1 && ap.map((p,i)=>(
        <ThreadCard key={p.id} process={p} threadIdx={i} expandedThreads={expandedThreads} expandedAgents={expandedAgents} expandedCalls={expandedCalls} onToggleThread={onToggleThread} onToggleAgent={onToggleAgent} onToggleCall={onToggleCall} />
      ))}
      {agenticTab === 2 && <UsageBreakdown processes={ap} />}
    </div>
  );
}